import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import { fetchInventoryAnalytics } from "../../apis/api.inventory";
import { queryInventoryAnalyticsKey } from "../../apis/queryKeys";
import Spinner from "../common/Spinner";

// Paleta suave y moderna (inspirada en dashboards de inventario).
const PALETTE = [
  "#34d399", // emerald
  "#38bdf8", // sky
  "#a78bfa", // violet
  "#fbbf24", // amber
  "#f87171", // coral
];

const ACCENT = {
  treatments: { stroke: "#10b981", fill: "#34d399", id: "invGradGreen" },
  supplies: { stroke: "#3b82f6", fill: "#38bdf8", id: "invGradBlue" },
};

// "2026-07" -> "jul 26"
const monthLabel = (m) => {
  if (!m || typeof m !== "string") return m;
  const d = new Date(`${m}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return m;
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-gray-800 mb-0.5">{monthLabel(label)}</p>
      <p className="text-gray-600">
        {payload[0].value} {unit}
      </p>
    </div>
  );
}

function ChartCard({ title, dotColor, children, footer }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: dotColor }} />
        <p className="text-sm font-semibold text-gray-800">{title}</p>
      </div>
      {children}
      {footer}
    </div>
  );
}

// Área con degradado y curva suave.
function AreaTrend({ data, dataKey, accent, unit }) {
  return (
    <ResponsiveContainer width="100%" height={210}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
        <defs>
          <linearGradient id={accent.id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent.fill} stopOpacity={0.35} />
            <stop offset="100%" stopColor={accent.fill} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis
          dataKey="month"
          tickFormatter={monthLabel}
          tickLine={false}
          axisLine={false}
          tick={{ fill: "#94a3b8", fontSize: 11 }}
          dy={6}
        />
        <YAxis
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={28}
          tick={{ fill: "#94a3b8", fontSize: 11 }}
        />
        <Tooltip content={<ChartTooltip unit={unit} />} cursor={{ stroke: "#e2e8f0" }} />
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={accent.stroke}
          strokeWidth={2.5}
          fill={`url(#${accent.id})`}
          dot={false}
          activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff" }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Barras tipo "píldora" con riel de fondo, punto de color y valor a la derecha.
function TopBars({ data, valueKey }) {
  const max = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  return (
    <div className="space-y-3 py-1">
      {data.map((d, i) => {
        const value = Number(d[valueKey]) || 0;
        const pct = Math.max(4, Math.round((value / max) * 100));
        const color = PALETTE[i % PALETTE.length];
        return (
          <div key={`${d.name}-${i}`} className="flex items-center gap-3">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: color }}
            />
            <span
              className="w-24 sm:w-32 truncate text-sm text-gray-700 shrink-0"
              title={d.name}
            >
              {d.name}
            </span>
            <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            <span className="w-10 text-right text-sm font-semibold text-gray-800 shrink-0">
              {value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ message }) {
  return <p className="text-xs text-gray-400 text-center py-12">{message}</p>;
}

// 4 gráficos: consumo de tratamientos, consumo de insumos, top 5 tratamientos, top 5 insumos.
export default function InventoryCharts() {
  const { t } = useBrandTranslation();
  const { ownerId } = useInventoryOwner();

  const { data, isLoading } = useQuery({
    queryKey: queryInventoryAnalyticsKey(ownerId),
    queryFn: () => fetchInventoryAnalytics(ownerId),
    enabled: !!ownerId,
  });

  if (isLoading) return <Spinner className="w-8 h-8 mx-auto my-6" />;

  const emptyMsg = t("inventory.charts.empty", "Todavía no hay datos suficientes para mostrar");
  const treatmentConsumption = data?.treatmentConsumption || [];
  const supplyConsumption = data?.supplyConsumption || [];
  const topTreatments = data?.topTreatments || [];
  const topSupplies = data?.topSupplies || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ChartCard
        title={t("inventory.charts.treatmentConsumption", "Consumo de tratamientos")}
        dotColor={ACCENT.treatments.stroke}
      >
        {treatmentConsumption.length > 0 ? (
          <AreaTrend
            data={treatmentConsumption}
            dataKey="count"
            accent={ACCENT.treatments}
            unit={t("inventory.charts.count", "Cantidad")}
          />
        ) : (
          <EmptyState message={emptyMsg} />
        )}
      </ChartCard>

      <ChartCard
        title={t("inventory.charts.supplyConsumption", "Consumo de insumos")}
        dotColor={ACCENT.supplies.stroke}
      >
        {supplyConsumption.length > 0 ? (
          <AreaTrend
            data={supplyConsumption}
            dataKey="quantity"
            accent={ACCENT.supplies}
            unit={t("inventory.charts.quantity", "Cantidad")}
          />
        ) : (
          <EmptyState message={emptyMsg} />
        )}
      </ChartCard>

      <ChartCard
        title={t("inventory.charts.topTreatments", "Top 5 tratamientos más utilizados")}
        dotColor={PALETTE[0]}
      >
        {topTreatments.length > 0 ? (
          <TopBars data={topTreatments} valueKey="count" />
        ) : (
          <EmptyState message={emptyMsg} />
        )}
      </ChartCard>

      <ChartCard
        title={t("inventory.charts.topSupplies", "Top 5 insumos más utilizados")}
        dotColor={PALETTE[1]}
      >
        {topSupplies.length > 0 ? (
          <TopBars data={topSupplies} valueKey="quantity" />
        ) : (
          <EmptyState message={emptyMsg} />
        )}
      </ChartCard>
    </div>
  );
}
